import React, { useEffect, useState } from 'react'
import useInput from 'src/hooks/useInput'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Typography
} from '@material-ui/core'
import {
  Error,
  VisibilityOffOutlined,
  VisibilityOutlined
} from '@material-ui/icons'
import alert from 'src/assets/images/password/alert.svg'
import padlock from 'src/assets/images/password/padlock.svg'
import Spinner from 'src/components/Spinner'
import {
  getPasswordResetToken,
  postResetPassword
} from 'src/services/api/WebApi/PasswordReset/passwordReset'
import useQueryString from 'src/hooks/useQueryString'
import { sendResetPasswordConfirmEmail } from 'src/services/api/EmailsApi/PasswordReset/forgotPassword'
import { getUserGeolocation } from 'src/services/api/External/GeolocationDb/geolocationDb'
import BreadcrumbsComponent from 'src/components/BreadcrumbsComponent/BreadcrumbsComponent'
import { useGlobalStyles } from 'src/mui/styles/globalStyles'
import { useStyles } from './ResetPasswordContent.styles'

type ContentProps = {
  token: string | null
  setLoading: (p: boolean) => void
  setSuccessReset: (p: boolean) => void
  setIsTokenValid: (p: boolean) => void
  newPasswordEqualsToOldPassword: boolean
  setNewPasswordEqualsToOldPassword: (p: boolean) => void
  loading: any
  password: any
  passwordRep: any
}

const ResetPasswordContent = () => {
  const query = useQueryString()
  const token = query.get('token')

  const password = useInput('')
  const passwordRep = useInput('')

  const [loading, setLoading] = useState(true)
  const [isTokenValid, setIsTokenValid] = useState(false)
  const [successReset, setSuccessReset] = useState(false)
  const [newPasswordEqualsToOldPassword, setNewPasswordEqualsToOldPassword] =
    useState(false)

  const classesGlobal = useGlobalStyles()

  const checkToken = async (token: any) => {
    await getPasswordResetToken(token)
      .then((response) => {
        if (response.status === 200 && !response.data.isUsed) {
          setIsTokenValid(true)
        }
      })
      .catch((err) => {
        console.log(err.response)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  useEffect(() => {
    checkToken(token).then()
  }, [])

  useEffect(() => {
    setNewPasswordEqualsToOldPassword(false)
  }, [password.value])

  return (
    <Box className={classesGlobal.containerPages}>
      {loading ? (
        <Spinner heightContainer="100vh" size={'60px'} />
      ) : isTokenValid ? (
        <Content
          token={token}
          setLoading={setLoading}
          setSuccessReset={setSuccessReset}
          setIsTokenValid={setIsTokenValid}
          newPasswordEqualsToOldPassword={newPasswordEqualsToOldPassword}
          setNewPasswordEqualsToOldPassword={setNewPasswordEqualsToOldPassword}
          loading={loading}
          password={password}
          passwordRep={passwordRep}
        />
      ) : successReset ? (
        <SuccessReset />
      ) : (
        <TokenInvalid />
      )}
    </Box>
  )
}

const Content = ({
  token,
  setLoading,
  setSuccessReset,
  setIsTokenValid,
  newPasswordEqualsToOldPassword,
  setNewPasswordEqualsToOldPassword,
  loading,
  password,
  passwordRep
}: ContentProps) => {
  const { t } = useTranslation()
  const [isEyeOpen, setIsEyeOpen] = useState(false)
  const [isEyeOpenRep, setIsEyeOpenRep] = useState(false)

  const [isPasswordEmpty, setIsPasswordEmpty] = useState(false)
  const [isPasswordRepEmpty, setIsPasswordRepEmpty] = useState(false)
  const [isPasswordRepDifferent, setIsPasswordRepDifferent] = useState(false)
  const [isPasswordWeak, setIsPasswordWeak] = useState(false)

  const classesGlobal = useGlobalStyles()
  const classes = useStyles()

  const checkIsPasswordWeak = (password: string) => {
    if (
      password.length < 8 ||
      password.search(/[a-z]/) < 0 ||
      password.search(/[A-Z]/) < 0 ||
      password.search(/[0-9]/) < 0
    ) {
      setIsPasswordWeak(true)
    } else {
      if (!token) {
        setIsTokenValid(false)
        return
      }
      try {
        submitPassword(token, password).then()
      } catch (error) {
        console.error(error)
      }
    }
  }

  const handlePasswordReset = () => {
    if (password.value.length === 0) {
      setIsPasswordEmpty(true)
    }
    if (passwordRep.value.length === 0) {
      setIsPasswordRepEmpty(true)
    }
    if (passwordRep.value.length > 0 && password.value.length > 0) {
      if (password.value !== passwordRep.value) {
        setIsPasswordRepDifferent(true)
      } else {
        checkIsPasswordWeak(password.value)
      }
    }
  }

  const handleEye = () => {
    setIsEyeOpen(!isEyeOpen)
  }

  const handleEyeRep = () => {
    setIsEyeOpenRep(!isEyeOpenRep)
  }

  const sendConfirmationEmail = async (email: string) => {
    const getUserGeolocationResponse = await getUserGeolocation()
    if (getUserGeolocationResponse.status === 200) {
      await sendResetPasswordConfirmEmail({
        email: email,
        ip: getUserGeolocationResponse.data.IPv4,
        location: `${getUserGeolocationResponse.data.city}, ${getUserGeolocationResponse.data.state}, ${getUserGeolocationResponse.data.country_name}`
      })
    }
  }

  const submitPassword = async (token: string, newPassword: string) => {
    setLoading(true)
    const formData = new FormData()
    formData.append('token', token)
    formData.append('newPassword', newPassword)
    postResetPassword(formData)
      .then((response) => {
        if (response.status === 200) {
          sendConfirmationEmail(response.data.email)
          setIsTokenValid(false)
          setSuccessReset(true)
        }
      })
      .catch((err) => {
        console.log(err)
        if (err.response.status === 422) {
          setNewPasswordEqualsToOldPassword(true)
        } else {
          setIsTokenValid(false)
        }
      })
      .finally(() => {
        setLoading(false)
      })
  }

  useEffect(() => {
    setIsPasswordEmpty(false)
    setIsPasswordWeak(false)
  }, [password.value])

  useEffect(() => {
    setIsPasswordRepEmpty(false)
  }, [passwordRep.value])

  useEffect(() => {
    setIsPasswordRepDifferent(false)
  }, [passwordRep.value, password.value])

  return (
    <Box className={classes.containerSubmitPassword}>
      <BreadcrumbsComponent
        crumbs={[
          { label: t('navbar.start'), path: '/' },
          { label: t('password.reset') }
        ]}
      />
      <Typography variant="h3" className={classesGlobal.titleSection}>
        {t('password.reset')}
      </Typography>
      <Typography variant="h5" style={{ fontWeight: 400 }}>
        {t('password.reset-info')}
      </Typography>
      <Box className={classes.contentInstruction}>
        <Error className={classes.IconInstruction} />
        <Typography>{t('password.instruction')}</Typography>
      </Box>

      <Box className={classes.contentInputSubmitPassword}>
        <Box className={classes.inputPasswordBox}>
          <Typography
            variant="h5"
            component="label"
            htmlFor="new-password-lsbd"
            className={classes.inputLabel}
          >
            {t('password.insert-password')}
          </Typography>
          <TextField
            variant="outlined"
            value={password.value}
            onChange={password.onChange}
            type={isEyeOpen ? 'text' : 'password'}
            placeholder={t('password.input-password')}
            id="new-password-lsbd"
            className={classes.input}
            label={null}
            error={
              isPasswordEmpty ||
              isPasswordWeak ||
              newPasswordEqualsToOldPassword
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleEye}
                    edge="end"
                    disableFocusRipple={true}
                  >
                    {isEyeOpen ? (
                      <VisibilityOutlined />
                    ) : (
                      <VisibilityOffOutlined />
                    )}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </Box>
        {isPasswordEmpty && (
          <Box className={classes.errorsBox}>
            <Error color="error" fontSize="small" />
            <Typography>{t('form.field-required')}</Typography>
          </Box>
        )}
        {isPasswordWeak && (
          <Box className={classes.errorsBox}>
            <Error color="error" fontSize="small" />
            <Typography>{t('password.weak')}</Typography>
          </Box>
        )}
        {newPasswordEqualsToOldPassword && (
          <Box className={classes.errorsBox}>
            <Error color="error" fontSize="small" />
            <Typography>{t('password.equals-to-old-password')}</Typography>
          </Box>
        )}
      </Box>

      <Box className={classes.contentInputSubmitPassword}>
        <Box className={classes.inputPasswordBox}>
          <Typography
            variant="h5"
            component="label"
            htmlFor="new-password-re-lsbd"
            className={classes.inputLabel}
          >
            {t('password.insert-password-rep')}
          </Typography>
          <TextField
            variant="outlined"
            value={passwordRep.value}
            onChange={passwordRep.onChange}
            type={isEyeOpenRep ? 'text' : 'password'}
            placeholder={t('password.input-password-rep')}
            id="new-password-re-lsbd"
            label={null}
            className={classes.input}
            error={isPasswordRepDifferent || isPasswordRepEmpty}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleEyeRep}
                    edge="end"
                    disableFocusRipple={true}
                  >
                    {isEyeOpenRep ? (
                      <VisibilityOutlined />
                    ) : (
                      <VisibilityOffOutlined />
                    )}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </Box>
        {isPasswordRepEmpty && (
          <Box className={classes.errorsBox}>
            <Error color="error" fontSize="small" />
            <Typography>{t('form.field-required')}</Typography>
          </Box>
        )}
        {isPasswordRepDifferent && (
          <Box className={classes.errorsBox}>
            <Error color="error" fontSize="small" />
            <Typography>{t('password.password-rep-invalid')}</Typography>
          </Box>
        )}
      </Box>

      <Button
        variant="contained"
        onClick={() => handlePasswordReset()}
        className={classes.submitButton}
        disableElevation
        disableRipple
      >
        {!loading ? (
          t('password.set-new-password')
        ) : (
          <CircularProgress size={16} />
        )}
      </Button>

      <Typography variant="body1" className={classes.needHelp}>
        {t('password.support')}
      </Typography>
    </Box>
  )
}

const TokenInvalid = () => {
  const history = useHistory()
  const { t } = useTranslation()
  const classesGlobal = useGlobalStyles()
  const classes = useStyles()

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  return (
    <Box>
      <BreadcrumbsComponent
        crumbs={[
          { label: t('navbar.start'), path: '/' },
          {
            label: t('login-modal.forgot-password')
          },
          { label: t('password.token-expired') }
        ]}
      />
      <Box
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          marginTop: 12
        }}
      >
        <img alt="alerte icon" src={alert} />
        <Typography className={classesGlobal.titleSection}>
          {t('password.token-invalid')}
        </Typography>
        <Typography
          variant="h5"
          style={{ fontWeight: 400, margin: '0 0 12px -24px' }}
        >
          {t('password.token-invalid-text1')}
        </Typography>
        <Typography
          component="ul"
          variant="h5"
          style={{ fontWeight: 400, margin: '0 3=px' }}
        >
          <li>{t('password.token-invalid-motive3')}</li>
          <li>{t('password.token-invalid-motive2')}</li>
          <li>{t('password.token-invalid-motive1')}</li>
        </Typography>
        <Button
          variant="contained"
          onClick={() => history.push('/forgot-password')}
          className={classes.submitButton}
          disableElevation
          disableRipple
        >
          {t('password.change')}
        </Button>

        <Typography variant="body1" className={classes.needHelp}>
          {t('password.support')}
        </Typography>
      </Box>
    </Box>
  )
}

const SuccessReset = () => {
  const { t } = useTranslation()
  const classesGlobal = useGlobalStyles()

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  return (
    <Box>
      <BreadcrumbsComponent
        crumbs={[
          { label: t('navbar.start'), path: '/' },
          {
            label: t('password.reset')
          },
          { label: t('password.password-changed') }
        ]}
      />
      <Box
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          marginTop: 12
        }}
      >
        <img alt="padlock icon" src={padlock} />
        <Typography className={classesGlobal.titleSection}>
          {t('password.success-ful-reset')}
        </Typography>
        <Typography variant="h5" style={{ fontWeight: 400 }}>
          {t('password.success-ful-reset-warning')}
        </Typography>
      </Box>
    </Box>
  )
}

export default ResetPasswordContent
